/**
 * Mission Studio React Query hooks.
 *
 * Hierarchical key namespace per project convention. Mutations
 * invalidate the closest parent key so the Studio's listing/detail
 * views update on copy/assign/edit without manual refetch glue.
 */

import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import { useBrowsingIdentity } from '@/roster/useBrowsingIdentity';

import {
  assignMission,
  getBeat,
  getBoardPostings,
  getGroupBeat,
  getOpportunities,
  inviteToMission,
  listJournal,
  listPendingInvites,
  reportMission,
  resolveBeat,
  respondToMissionInvite,
  castGroupVote,
  submitGroupPick,
  copyNode,
  copySubtree,
  copyTemplate,
  createGiver,
  createMissionTemplate,
  deleteGiver,
  deleteMissionInstance,
  getMissionTemplate,
  listGivers,
  listMissionCategories,
  listMissionNodes,
  listMissionOptions,
  listMissionRoutes,
  listMissionTemplates,
  listPredicateLeaves,
  listRouteCandidates,
  listRouteRewards,
  patchGiver,
  patchMissionNode,
  patchMissionTemplate,
  takeBoardPosting,
  tellTale,
} from './api';
import type { PredicateLeaf, PredicateLeafParam, PredicateParamType } from './api';
export type { PredicateLeaf, PredicateLeafParam, PredicateParamType };
import type {
  BeatView,
  GroupBeatResult,
  JournalEntry,
  PendingMissionInvite,
  MissionCategory,
  MissionGiver,
  MissionGiverRequest,
  MissionInstance,
  MissionNode,
  MissionOption,
  MissionOptionRoute,
  MissionOptionRouteCandidate,
  MissionOptionRouteReward,
  MissionTemplate,
  MissionTemplateDetail,
  MissionTemplateFilters,
  PaginatedResponse,
  ReportStyle,
} from './types';

export const missionKeys = {
  all: ['missions'] as const,
  templates: () => [...missionKeys.all, 'templates'] as const,
  templateList: (filters: MissionTemplateFilters & { page?: number }) =>
    [...missionKeys.templates(), 'list', filters] as const,
  templateDetail: (id: number) => [...missionKeys.templates(), 'detail', id] as const,
  nodes: () => [...missionKeys.all, 'nodes'] as const,
  nodesFor: (filters: object) => [...missionKeys.nodes(), filters] as const,
  options: () => [...missionKeys.all, 'options'] as const,
  optionsFor: (filters: object) => [...missionKeys.options(), filters] as const,
  routes: () => [...missionKeys.all, 'routes'] as const,
  routesFor: (filters: object) => [...missionKeys.routes(), filters] as const,
  candidates: () => [...missionKeys.all, 'candidates'] as const,
  candidatesFor: (filters: object) => [...missionKeys.candidates(), filters] as const,
  rewards: () => [...missionKeys.all, 'rewards'] as const,
  rewardsFor: (filters: object) => [...missionKeys.rewards(), filters] as const,
  predicateLeaves: () => [...missionKeys.all, 'predicate-leaves'] as const,
  categories: () => [...missionKeys.all, 'categories'] as const,
  givers: () => [...missionKeys.all, 'givers'] as const,
  // Player-scoped read keys carry the tab's browsing identity (#3479) as a
  // trailing `entryId` so a browsing-identity switch refetches; the bare
  // parent keys remain for prefix invalidation in the mutation hooks.
  journal: () => [...missionKeys.all, 'journal'] as const,
  journalFor: (entryId: number | null) => [...missionKeys.journal(), { entryId }] as const,
  pendingInvites: () => [...missionKeys.all, 'pending-invites'] as const,
  pendingInvitesFor: (entryId: number | null) =>
    [...missionKeys.pendingInvites(), { entryId }] as const,
  opportunities: () => [...missionKeys.all, 'opportunities'] as const,
  opportunitiesFor: (entryId: number | null) =>
    [...missionKeys.opportunities(), { entryId }] as const,
  // roomKey threads the player's current room into the key so a move
  // refetches liveness — the server computes "live here" from the puppet.
  beat: (instanceId: number, roomKey: string, entryId: number | null) =>
    [...missionKeys.all, 'beat', instanceId, roomKey, { entryId }] as const,
  // Same roomKey threading for the group beat (options are per-viewer +
  // location-gated).
  groupBeat: (instanceId: number, roomKey: string, entryId: number | null) =>
    [...missionKeys.all, 'group-beat', instanceId, roomKey, { entryId }] as const,
  giversFor: (filters: object) => [...missionKeys.givers(), filters] as const,
  boardPostings: (boardObjectId: number) =>
    [...missionKeys.all, 'board-postings', boardObjectId] as const,
  boardPostingsFor: (boardObjectId: number, entryId: number | null) =>
    [...missionKeys.boardPostings(boardObjectId), { entryId }] as const,
};

const FIVE_MINUTES = 5 * 60 * 1000;

export function useMissionTemplates(
  filters: MissionTemplateFilters & { page?: number; page_size?: number } = {}
): UseQueryResult<PaginatedResponse<MissionTemplate>> {
  return useQuery({
    queryKey: missionKeys.templateList(filters),
    queryFn: () => listMissionTemplates(filters),
    staleTime: 30_000,
    // No throwOnError: MissionBrowserPage handles isError inline so a list
    // fetch failure shows a "couldn't load" card rather than crashing to the
    // global ErrorBoundary. Other drill-down hooks still use throwOnError —
    // tracked as a follow-up to align them; out of scope for this PR.
  });
}

export function useMissionTemplate(id: number | undefined): UseQueryResult<MissionTemplateDetail> {
  return useQuery({
    queryKey: missionKeys.templateDetail(id ?? 0),
    queryFn: () => getMissionTemplate(id as number),
    enabled: id !== undefined && id !== 0 && Number.isFinite(id),
  });
}

export function useMissionNodes(
  filters: {
    template?: number;
    is_entry?: boolean;
    needs_rewrite?: boolean;
  } = {}
): UseQueryResult<PaginatedResponse<MissionNode>> {
  return useQuery({
    queryKey: missionKeys.nodesFor(filters),
    queryFn: () => listMissionNodes(filters),
    // Consumers check isError and render inline so a fetch failure doesn't
    // nuke the whole drill-down view.
  });
}

export function useMissionOptions(
  filters: { node?: number; template?: number; needs_rewrite?: boolean } = {}
): UseQueryResult<PaginatedResponse<MissionOption>> {
  return useQuery({
    queryKey: missionKeys.optionsFor(filters),
    queryFn: () => listMissionOptions(filters),
    enabled: Boolean(filters.node ?? filters.template),
    // Consumers check isError and render inline so a fetch failure doesn't
    // nuke the whole drill-down view.
  });
}

export function useMissionRoutes(
  filters: { option?: number; template?: number; needs_rewrite?: boolean } = {}
): UseQueryResult<PaginatedResponse<MissionOptionRoute>> {
  return useQuery({
    queryKey: missionKeys.routesFor(filters),
    queryFn: () => listMissionRoutes(filters),
    enabled: Boolean(filters.option ?? filters.template),
    // Consumers check isError and render inline so a fetch failure doesn't
    // nuke the whole drill-down view.
  });
}

export function useRouteCandidates(
  filters: { route?: number } = {}
): UseQueryResult<PaginatedResponse<MissionOptionRouteCandidate>> {
  return useQuery({
    queryKey: missionKeys.candidatesFor(filters),
    queryFn: () => listRouteCandidates(filters),
    enabled: Boolean(filters.route),
    // Consumers check isError and render inline so a fetch failure doesn't
    // nuke the whole drill-down view.
  });
}

export function useRouteRewards(
  filters: { route?: number; candidate?: number } = {}
): UseQueryResult<PaginatedResponse<MissionOptionRouteReward>> {
  return useQuery({
    queryKey: missionKeys.rewardsFor(filters),
    queryFn: () => listRouteRewards(filters),
    enabled: Boolean(filters.route ?? filters.candidate),
    // Consumers check isError and render inline so a fetch failure doesn't
    // nuke the whole drill-down view.
  });
}

export function usePredicateLeaves(): UseQueryResult<PredicateLeaf[]> {
  return useQuery({
    queryKey: missionKeys.predicateLeaves(),
    queryFn: listPredicateLeaves,
    staleTime: FIVE_MINUTES,
    // Consumers check isError and render inline so a fetch failure doesn't
    // nuke the whole drill-down view.
  });
}

// ---------------------------------------------------------------------------
// Mutations — invalidate the closest parent key on success.
// ---------------------------------------------------------------------------

export function usePatchMissionTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: Partial<MissionTemplate> }) =>
      patchMissionTemplate(id, body),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: missionKeys.templateDetail(id) });
      qc.invalidateQueries({ queryKey: missionKeys.templates() });
    },
  });
}

export function usePatchMissionNode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: Partial<MissionNode> }) =>
      patchMissionNode(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: missionKeys.nodes() });
    },
  });
}

export function useCopyTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, new_name }: { id: number; new_name?: string }) =>
      copyTemplate(id, { new_name }),
    onSuccess: () => qc.invalidateQueries({ queryKey: missionKeys.templates() }),
  });
}

export function useCopyNode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, new_key }: { id: number; new_key: string }) => copyNode(id, { new_key }),
    onSuccess: () => qc.invalidateQueries({ queryKey: missionKeys.nodes() }),
  });
}

export function useCopySubtree() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, new_key_prefix }: { id: number; new_key_prefix: string }) =>
      copySubtree(id, { new_key_prefix }),
    onSuccess: () => qc.invalidateQueries({ queryKey: missionKeys.nodes() }),
  });
}

export function useAssignMission() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, character }: { id: number; character: number }) =>
      assignMission(id, { character }),
    onSuccess: (_inst: MissionInstance, { id }) => {
      qc.invalidateQueries({ queryKey: missionKeys.templateDetail(id) });
    },
  });
}

export function useDeleteMissionInstance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteMissionInstance(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: missionKeys.templates() }),
  });
}

export function useCreateMissionTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<MissionTemplate>) => createMissionTemplate(body),
    onSuccess: (created) => {
      // Prime the detail cache with the create response so MissionCanvasPage
      // renders instantly on the post-create navigate instead of firing a
      // cold fetch and showing a spinner. Invalidate the list so the browser
      // shows the new row.
      qc.setQueryData(missionKeys.templateDetail(created.id), created);
      qc.invalidateQueries({ queryKey: missionKeys.templates() });
    },
  });
}

export function useMissionCategories(): UseQueryResult<PaginatedResponse<MissionCategory>> {
  return useQuery({
    queryKey: missionKeys.categories(),
    queryFn: listMissionCategories,
    staleTime: FIVE_MINUTES,
    // Intentionally no throwOnError here: the picker consumers (CreateMissionPage,
    // EditCategoriesDialog) check isError and render inline so a categories
    // fetch failure doesn't nuke the user's half-filled form.
  });
}

// ---------------------------------------------------------------------------
// MissionGiver hooks (#729 — trigger-based giver editor)
// ---------------------------------------------------------------------------

export function useGivers(
  filters: { giver_kind?: string; is_active?: boolean; name?: string; page_size?: number } = {}
): UseQueryResult<PaginatedResponse<MissionGiver>> {
  return useQuery({
    queryKey: missionKeys.giversFor(filters),
    queryFn: () => listGivers(filters),
    staleTime: 30_000,
  });
}

export function useCreateGiver() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<MissionGiverRequest>) => createGiver(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: missionKeys.givers() }),
  });
}

export function usePatchGiver() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: Partial<MissionGiverRequest> }) =>
      patchGiver(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: missionKeys.givers() }),
  });
}

export function useDeleteGiver() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteGiver(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: missionKeys.givers() }),
  });
}

// ---------------------------------------------------------------------------
// #885 player journal/beat hooks.
//
// Every player-scoped hook below acts as the tab's browsing identity (#3479):
// `useBrowsingIdentity().entryId` rides along as an `entry_id` query param.
// Null (no identity hydrated) omits the param, so the server falls back to
// the account's durable selection. A retired-mid-session id 403s by design:
// no client-side fallback; the server answers.
// ---------------------------------------------------------------------------

export function useJournal(): UseQueryResult<PaginatedResponse<JournalEntry>> {
  const { entryId } = useBrowsingIdentity();
  return useQuery({
    queryKey: missionKeys.journalFor(entryId),
    queryFn: () => listJournal(entryId),
  });
}

/** Pending mission invites for the puppet (#audit2) — surfaces even with an empty journal. */
export function usePendingInvites(): UseQueryResult<PendingMissionInvite[]> {
  const { entryId } = useBrowsingIdentity();
  return useQuery({
    queryKey: missionKeys.pendingInvitesFor(entryId),
    queryFn: () => listPendingInvites(entryId),
    refetchInterval: 15_000,
  });
}

export function useOpportunities() {
  const { entryId } = useBrowsingIdentity();
  return useQuery({
    queryKey: missionKeys.opportunitiesFor(entryId),
    queryFn: () => getOpportunities(entryId),
  });
}

/**
 * The current beat for one run. `roomKey` is any stable identifier of the
 * player's current room (name from game state is fine) — moving rooms
 * changes the key, which refetches the live-here option set.
 */
export function useBeat(
  instanceId: number | undefined,
  roomKey: string
): UseQueryResult<BeatView | null> {
  const { entryId } = useBrowsingIdentity();
  return useQuery({
    queryKey: missionKeys.beat(instanceId ?? 0, roomKey, entryId),
    queryFn: () => getBeat(instanceId as number, entryId),
    enabled: instanceId !== undefined,
    // Liveness (2026-07 audit): a beat can be resolved by another participant
    // or fast-forwarded by an external act — nothing pushes that, so poll
    // while the card is mounted.
    refetchInterval: 10_000,
  });
}

export function useResolveBeat() {
  const qc = useQueryClient();
  const { entryId } = useBrowsingIdentity();
  return useMutation({
    mutationFn: ({
      instanceId,
      option_id,
      approach_id,
    }: {
      instanceId: number;
      option_id: number;
      approach_id?: number | null;
    }) => resolveBeat(instanceId, { option_id, approach_id }, entryId),
    onSuccess: () => {
      // Position, deeds, status, and liveness all changed.
      qc.invalidateQueries({ queryKey: missionKeys.journal() }).catch(() => {});
      qc.invalidateQueries({ queryKey: [...missionKeys.all, 'beat'] }).catch(() => {});
    },
  });
}

export function useTellTale() {
  const qc = useQueryClient();
  const { entryId } = useBrowsingIdentity();
  return useMutation({
    mutationFn: ({ instanceId, text }: { instanceId: number; text: string }) =>
      tellTale(instanceId, text, entryId),
    onSuccess: () => qc.invalidateQueries({ queryKey: missionKeys.journal() }).catch(() => {}),
  });
}

/** Report a RESOLVED run's outcome to its report-to Functionary (#1753/#3040). */
export function useReportMission() {
  const qc = useQueryClient();
  const { entryId } = useBrowsingIdentity();
  return useMutation({
    mutationFn: ({ instanceId, style }: { instanceId: number; style: ReportStyle }) =>
      reportMission(instanceId, style, entryId),
    onSuccess: () => qc.invalidateQueries({ queryKey: missionKeys.journal() }).catch(() => {}),
  });
}

/**
 * Postings on a notice-board room object (#2044/#3044). Disabled until a board
 * dialog is actually open — `boardObjectId` is only meaningful then.
 */
export function useBoardPostings(boardObjectId: number | null) {
  const { entryId } = useBrowsingIdentity();
  return useQuery({
    queryKey: missionKeys.boardPostingsFor(boardObjectId ?? 0, entryId),
    queryFn: () => getBoardPostings(boardObjectId as number, entryId),
    enabled: boardObjectId != null,
  });
}

/** Take a posting off a notice board — re-runs eligibility server-side (#2044/#3044). */
export function useTakeBoardPosting(boardObjectId: number) {
  const qc = useQueryClient();
  const { entryId } = useBrowsingIdentity();
  return useMutation({
    mutationFn: (templateId: number) => takeBoardPosting(boardObjectId, templateId, entryId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: missionKeys.boardPostings(boardObjectId) }).catch(() => {});
      qc.invalidateQueries({ queryKey: missionKeys.journal() }).catch(() => {});
    },
  });
}

// ---------------------------------------------------------------------------
// #1036 group beat + #887 invite hooks (#2049).
// ---------------------------------------------------------------------------

export function useGroupBeat(
  instanceId: number | undefined,
  roomKey: string
): UseQueryResult<GroupBeatResult> {
  const { entryId } = useBrowsingIdentity();
  return useQuery({
    queryKey: missionKeys.groupBeat(instanceId ?? 0, roomKey, entryId),
    queryFn: () => getGroupBeat(instanceId as number, entryId),
    enabled: instanceId !== undefined,
    // Liveness (2026-07 audit): group ballots are inherently multiplayer —
    // without a poll, participant B never saw A's vote land, and the
    // "waiting for the server to resolve" countdown state waited forever
    // (only a tab refocus ever refetched). 5s matches the rituals inbox.
    refetchInterval: 5_000,
  });
}

export function useSubmitGroupPick() {
  const qc = useQueryClient();
  const { entryId } = useBrowsingIdentity();
  return useMutation({
    mutationFn: ({
      instanceId,
      option_id,
      approach_id,
    }: {
      instanceId: number;
      option_id: number;
      approach_id?: number | null;
    }) => submitGroupPick(instanceId, { option_id, approach_id }, entryId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...missionKeys.all, 'group-beat'] }).catch(() => {});
      qc.invalidateQueries({ queryKey: missionKeys.journal() }).catch(() => {});
    },
  });
}

export function useCastGroupVote() {
  const qc = useQueryClient();
  const { entryId } = useBrowsingIdentity();
  return useMutation({
    mutationFn: ({ instanceId, option_id }: { instanceId: number; option_id: number }) =>
      castGroupVote(instanceId, { option_id }, entryId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...missionKeys.all, 'group-beat'] }).catch(() => {});
      qc.invalidateQueries({ queryKey: missionKeys.journal() }).catch(() => {});
    },
  });
}

export function useInviteToMission() {
  const qc = useQueryClient();
  const { entryId } = useBrowsingIdentity();
  return useMutation({
    mutationFn: ({
      instanceId,
      invitee_character_id,
    }: {
      instanceId: number;
      invitee_character_id: number;
    }) => inviteToMission(instanceId, { invitee_character_id }, entryId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: missionKeys.journal() }).catch(() => {});
      qc.invalidateQueries({ queryKey: missionKeys.pendingInvites() }).catch(() => {});
    },
  });
}

export function useRespondToMissionInvite() {
  const qc = useQueryClient();
  const { entryId } = useBrowsingIdentity();
  return useMutation({
    mutationFn: (body: { invite_id: number; response: 'accept' | 'decline' }) =>
      respondToMissionInvite(body, entryId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: missionKeys.journal() }).catch(() => {});
      qc.invalidateQueries({ queryKey: missionKeys.pendingInvites() }).catch(() => {});
    },
  });
}
